import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "tss-react/mui";
import { alpha } from "@mui/material/styles";
import toast from "react-hot-toast";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ContactModal from "../ContactModal";
import MarkdownWrapper from "../MarkdownWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";

const DRAWER_WIDTH = 370;

const useStyles = makeStyles()((theme) => ({
  drawerPaper: {
    width: DRAWER_WIDTH,
    display: "flex",
    flexDirection: "column",
    borderLeft: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    overflow: "hidden",
  },

  header: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    minHeight: 56,
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },

  scrollContent: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },

  profileSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(3, 2, 2),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(1),
  },

  avatarWrapper: {
    position: "relative",
  },

  avatar: {
    width: 88,
    height: 88,
    fontSize: "2rem",
    backgroundColor: theme.palette.primary.main,
  },

  groupBadge: {
    position: "absolute",
    bottom: 2,
    right: 2,
    backgroundColor: theme.palette.background.paper,
    borderRadius: "50%",
    padding: 2,
    display: "flex",
    "& svg": {
      fontSize: 14,
      color: theme.palette.text.secondary,
    },
  },

  contactName: {
    fontWeight: 700,
    fontSize: "1.1rem",
    textAlign: "center",
    lineHeight: 1.3,
  },

  actionRow: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    flexWrap: "wrap",
    justifyContent: "center",
  },

  actionBtn: {
    borderRadius: 20,
    textTransform: "none",
    fontSize: "0.78rem",
    padding: theme.spacing(0.5, 1.5),
  },

  section: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },

  sectionTitle: {
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.25),
  },

  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 0),
  },

  infoIcon: {
    color: theme.palette.text.secondary,
    fontSize: "1.1rem",
    flexShrink: 0,
  },

  infoText: {
    flex: 1,
    fontSize: "0.875rem",
    wordBreak: "break-all",
  },

  extraField: {
    padding: theme.spacing(0.75, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(0.75),
    border: `1px solid ${theme.palette.divider}`,
  },

  extraFieldLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: theme.palette.text.secondary,
    marginBottom: 2,
  },

  // ── Media grid ────────────────────────────────────────────────────────────
  mediaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 3,
  },

  mediaThumb: {
    aspectRatio: "1",
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    position: "relative",
    cursor: "pointer",
    backgroundColor: theme.palette.action.hover,
    "&:hover $mediaOverlay": { opacity: 1 },
  },

  mediaOverlay: {
    position: "absolute",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0,
    transition: "opacity 0.15s",
  },

  docRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(0.75),
    cursor: "pointer",
    textDecoration: "none",
    color: "inherit",
    "&:hover": { backgroundColor: theme.palette.action.hover },
  },

  linkRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(0.75),
    textDecoration: "none",
    color: "inherit",
    "&:hover": { backgroundColor: theme.palette.action.hover },
  },

  emptyState: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    padding: theme.spacing(3, 0),
    fontSize: "0.875rem",
  },

  // ── Toggle row (mute / block) ─────────────────────────────────────────────
  toggleRow: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0.5, 2),
    backgroundColor: theme.palette.background.paper,
  },

  toggleLabel: {
    flex: 1,
    fontSize: "0.875rem",
    fontWeight: 500,
  },

  // ── Groups in common ─────────────────────────────────────────────────────
  groupItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    padding: theme.spacing(0.75, 0),
  },

  groupAvatar: {
    width: 36,
    height: 36,
    fontSize: "0.85rem",
    backgroundColor: alpha(theme.palette.primary.main, 0.15),
    color: theme.palette.primary.main,
    flexShrink: 0,
  },

  // ── Block danger zone ─────────────────────────────────────────────────────
  blockSection: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },

  blockRow: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    gap: theme.spacing(1.5),
    cursor: "pointer",
    "&:hover": { backgroundColor: theme.palette.action.hover },
  },
}));

// ── Helpers ────────────────────────────────────────────────────────────────────

const CopyButton = ({ value, classes }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <Tooltip title={copied ? "Copied!" : "Copy"} placement="top">
      <IconButton size="small" onClick={handleCopy} sx={{ p: "4px", opacity: 1 }}>
        <ContentCopyIcon sx={{ fontSize: 14, color: copied ? "primary.main" : "text.secondary" }} />
      </IconButton>
    </Tooltip>
  );
};

const getFilename = (url) => {
  try { return decodeURIComponent(url.split("/").pop().split("?")[0]) || "File"; }
  catch { return "File"; }
};

const truncateUrl = (url, max = 48) =>
  url.length > max ? url.slice(0, max) + "…" : url;

// ── MediaSection ───────────────────────────────────────────────────────────────

const MediaSection = ({ contactId, classes }) => {
  const [tab, setTab] = useState("media");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!contactId) return;
    setLoading(true);
    api.get(`/contacts/${contactId}/media`)
      .then(({ data: d }) => setData(d))
      .catch(toastError)
      .finally(() => setLoading(false));
  }, [contactId]);

  const images = data?.images ?? [];
  const documents = data?.documents ?? [];
  const links = data?.links ?? [];

  const counts = {
    media: images.length,
    docs: documents.length,
    links: links.length,
  };

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="fullWidth"
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          minHeight: 36,
          "& .MuiTab-root": { minHeight: 36, py: 0, fontSize: "0.75rem", textTransform: "none", fontWeight: 600 },
        }}
      >
        <Tab
          label={`Media${counts.media ? ` (${counts.media})` : ""}`}
          value="media"
          icon={<ImageOutlinedIcon sx={{ fontSize: 15 }} />}
          iconPosition="start"
        />
        <Tab
          label={`Docs${counts.docs ? ` (${counts.docs})` : ""}`}
          value="docs"
          icon={<InsertDriveFileOutlinedIcon sx={{ fontSize: 15 }} />}
          iconPosition="start"
        />
        <Tab
          label={`Links${counts.links ? ` (${counts.links})` : ""}`}
          value="links"
          icon={<LinkOutlinedIcon sx={{ fontSize: 15 }} />}
          iconPosition="start"
        />
      </Tabs>

      <Box className={classes.section}>
        {loading ? (
          tab === "media" ? (
            <Box className={classes.mediaGrid}>
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} variant="rounded" sx={{ aspectRatio: "1", height: "auto" }} />
              ))}
            </Box>
          ) : (
            [1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={44} sx={{ mb: 0.75 }} />)
          )
        ) : tab === "media" ? (
          images.length === 0 ? (
            <Typography className={classes.emptyState}>No photos or videos</Typography>
          ) : (
            <Box className={classes.mediaGrid}>
              {images.map((m) => (
                <Box
                  key={m.id}
                  className={classes.mediaThumb}
                  component="a"
                  href={m.mediaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {m.mediaType === "video" ? (
                    <>
                      <Box
                        component="video"
                        src={m.mediaUrl}
                        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                      <Box className={classes.mediaOverlay}>
                        <PlayCircleOutlineIcon sx={{ color: "#fff", fontSize: 28 }} />
                      </Box>
                    </>
                  ) : (
                    <Box
                      component="img"
                      src={m.mediaUrl}
                      alt=""
                      sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                  )}
                </Box>
              ))}
            </Box>
          )
        ) : tab === "docs" ? (
          documents.length === 0 ? (
            <Typography className={classes.emptyState}>No documents</Typography>
          ) : (
            documents.map((m) => (
              <Box
                key={m.id}
                className={classes.docRow}
                component="a"
                href={m.mediaUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ArticleOutlinedIcon sx={{ fontSize: 22, color: "primary.main", flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {getFilename(m.mediaUrl)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {m.mediaType}
                  </Typography>
                </Box>
                <OpenInNewIcon sx={{ fontSize: 14, color: "text.disabled", flexShrink: 0 }} />
              </Box>
            ))
          )
        ) : (
          links.length === 0 ? (
            <Typography className={classes.emptyState}>No links</Typography>
          ) : (
            links.map((l, i) => (
              <Box
                key={`${l.id}-${i}`}
                className={classes.linkRow}
                component="a"
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <LinkOutlinedIcon sx={{ fontSize: 16, color: "primary.main", flexShrink: 0, mt: "2px" }} />
                <Typography variant="body2" sx={{ flex: 1, wordBreak: "break-all", fontSize: "0.8rem" }}>
                  {truncateUrl(l.url)}
                </Typography>
                <OpenInNewIcon sx={{ fontSize: 13, color: "text.disabled", flexShrink: 0, mt: "2px" }} />
              </Box>
            ))
          )
        )}
      </Box>
    </Box>
  );
};

// ── GroupsSection ──────────────────────────────────────────────────────────────

const GroupsSection = ({ contactId, classes }) => {
  const [groups, setGroups] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!contactId) return;
    setLoading(true);
    api.get(`/contacts/${contactId}/groups`)
      .then(({ data }) => setGroups(data))
      .catch(toastError)
      .finally(() => setLoading(false));
  }, [contactId]);

  if (!loading && (!groups || groups.length === 0)) return null;

  return (
    <>
      <Divider />
      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>
          Groups in Common{groups ? ` (${groups.length})` : ""}
        </Typography>
        {loading ? (
          [1, 2].map(i => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 0.75 }}>
              <Skeleton variant="circular" width={36} height={36} />
              <Skeleton width="60%" height={16} />
            </Box>
          ))
        ) : (
          groups.map(g => (
            <Box key={g.id} className={classes.groupItem}>
              <Avatar
                src={g.profilePicUrl}
                className={classes.groupAvatar}
              >
                <GroupOutlinedIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Typography variant="body2" fontWeight={500} noWrap sx={{ flex: 1 }}>
                {g.name}
              </Typography>
            </Box>
          ))
        )}
      </Box>
    </>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

const ContactDrawer = ({ open, handleDrawerClose, contact, loading, ticket }) => {
  const { classes } = useStyles();
  const { user } = React.useContext(AuthContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [fullContact, setFullContact] = useState(null);
  const [newTicketLoading, setNewTicketLoading] = useState(false);
  const [muteLoading, setMuteLoading] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);

  useEffect(() => {
    if (!open || !contact?.id) { setFullContact(null); return; }
    api.get(`/contacts/${contact.id}`)
      .then(({ data }) => setFullContact(data))
      .catch(() => {});
  }, [open, contact?.id]);

  const displayContact = fullContact || contact;
  const isAdmin = user?.profile === "admin" || user?.profile === "superadmin";
  const waNumber = displayContact?.number?.replace(/\D/g, "");

  const handleNewTicket = async () => {
    if (!contact?.id) return;
    setNewTicketLoading(true);
    try {
      const { data } = await api.post("/tickets", {
        contactId: contact.id,
        status: "open",
        userId: user.id,
      });
      handleDrawerClose();
      window.location.href = `/tickets/${data.id}`;
    } catch (err) {
      toastError(err);
    } finally {
      setNewTicketLoading(false);
    }
  };

  const handleMuteToggle = async () => {
    if (!displayContact?.id) return;
    setMuteLoading(true);
    try {
      const { data } = await api.put(`/contacts/${displayContact.id}/mute`);
      setFullContact(prev => ({ ...(prev || displayContact), muted: data.muted }));
      toast.success(data.muted ? "Notifications muted" : "Notifications unmuted");
    } catch (err) {
      toastError(err);
    } finally {
      setMuteLoading(false);
    }
  };

  const handleBlockToggle = async () => {
    if (!displayContact?.id) return;
    setBlockLoading(true);
    try {
      const { data } = await api.put(`/contacts/${displayContact.id}/block`);
      setFullContact(prev => ({ ...(prev || displayContact), blocked: data.blocked }));
      toast.success(data.blocked ? "Contact blocked" : "Contact unblocked");
    } catch (err) {
      toastError(err);
    } finally {
      setBlockLoading(false);
    }
  };

  return (
    <>
      <Drawer
        variant="temporary"
        anchor="right"
        open={open}
        onClose={handleDrawerClose}
        PaperProps={{ style: { position: "absolute" } }}
        BackdropProps={{ style: { position: "absolute" } }}
        ModalProps={{
          container: document.getElementById("drawer-container"),
          style: { position: "absolute" },
          keepMounted: true,
        }}
        classes={{ paper: classes.drawerPaper }}
      >
        {/* Header */}
        <Box className={classes.header}>
          <IconButton onClick={handleDrawerClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle2" fontWeight={600} sx={{ ml: 1 }}>
            Contact Details
          </Typography>
        </Box>

        {loading ? (
          <DrawerSkeleton classes={classes} />
        ) : (
          <Box className={classes.scrollContent}>
            {/* ── Profile ── */}
            <Box className={classes.profileSection}>
              <Box className={classes.avatarWrapper}>
                <Avatar
                  alt={displayContact?.name}
                  src={displayContact?.profilePicUrl}
                  className={classes.avatar}
                >
                  {displayContact?.name?.[0]?.toUpperCase()}
                </Avatar>
                {displayContact?.isGroup && (
                  <Box className={classes.groupBadge}>
                    <GroupOutlinedIcon />
                  </Box>
                )}
              </Box>

              <Typography className={classes.contactName}>
                {displayContact?.name}
              </Typography>

              {displayContact?.isGroup && (
                <Chip label="Group" size="small" variant="outlined" />
              )}

              {displayContact?.blocked && (
                <Chip
                  label="Blocked"
                  size="small"
                  color="error"
                  icon={<BlockOutlinedIcon />}
                  sx={{ "& .MuiChip-label": { fontSize: "0.7rem" } }}
                />
              )}

              {/* Action buttons */}
              <Box className={classes.actionRow}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditOutlinedIcon />}
                  className={classes.actionBtn}
                  onClick={() => setModalOpen(true)}
                >
                  Edit
                </Button>
                {waNumber && !displayContact?.isGroup && (
                  <Tooltip title="Open in WhatsApp">
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={<WhatsAppIcon />}
                      className={classes.actionBtn}
                      component="a"
                      href={`https://wa.me/${waNumber}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Chat
                    </Button>
                  </Tooltip>
                )}
                {isAdmin && (
                  <Tooltip title="Start new ticket">
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={newTicketLoading ? <CircularProgress size={12} /> : <AddCommentOutlinedIcon />}
                      className={classes.actionBtn}
                      onClick={handleNewTicket}
                      disabled={newTicketLoading}
                    >
                      New
                    </Button>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {/* ── Contact Info ── */}
            <Box className={classes.section}>
              <Typography className={classes.sectionTitle}>Contact Info</Typography>
              {displayContact?.number && (
                <Box className={classes.infoRow}>
                  <PhoneOutlinedIcon className={classes.infoIcon} />
                  <Typography className={classes.infoText}>{displayContact.number}</Typography>
                  <CopyButton value={displayContact.number} classes={classes} />
                  {waNumber && (
                    <Tooltip title="Call">
                      <IconButton size="small" component="a" href={`tel:${displayContact.number}`} sx={{ p: "4px" }}>
                        <OpenInNewIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}
              {displayContact?.email && (
                <Box className={classes.infoRow}>
                  <EmailOutlinedIcon className={classes.infoIcon} />
                  <Typography className={classes.infoText}>{displayContact.email}</Typography>
                  <CopyButton value={displayContact.email} classes={classes} />
                </Box>
              )}
              {!displayContact?.number && !displayContact?.email && (
                <Typography variant="caption" color="text.secondary">No contact info available</Typography>
              )}
            </Box>

            {/* ── Custom fields ── */}
            {displayContact?.extraInfo?.length > 0 && (
              <>
                <Divider />
                <Box className={classes.section}>
                  <Typography className={classes.sectionTitle}>
                    {i18n.t("contactDrawer.extraInfo")}
                  </Typography>
                  {displayContact.extraInfo.map(info => (
                    <Box key={info.id} className={classes.extraField}>
                      <Typography className={classes.extraFieldLabel}>{info.name}</Typography>
                      <Typography variant="body2" component="div">
                        <MarkdownWrapper>{info.value}</MarkdownWrapper>
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {/* ── Mute Notifications ── */}
            <Divider />
            <Box className={classes.toggleRow}>
              <NotificationsOffOutlinedIcon sx={{ fontSize: 20, color: "text.secondary", mr: 1.5, flexShrink: 0 }} />
              <Typography className={classes.toggleLabel}>Mute Notifications</Typography>
              {muteLoading ? (
                <CircularProgress size={18} sx={{ mr: 1 }} />
              ) : (
                <Switch
                  size="small"
                  checked={!!displayContact?.muted}
                  onChange={handleMuteToggle}
                  color="primary"
                />
              )}
            </Box>

            {/* ── Media, Links & Docs ── */}
            <Divider />
            {displayContact?.id && (
              <MediaSection contactId={displayContact.id} classes={classes} />
            )}

            {/* ── Groups in Common ── */}
            {displayContact?.id && !displayContact?.isGroup && (
              <GroupsSection contactId={displayContact.id} classes={classes} />
            )}

            {/* ── Block Contact ── */}
            <Divider />
            <Box className={classes.blockSection}>
              <Box
                className={classes.blockRow}
                onClick={blockLoading ? undefined : handleBlockToggle}
                sx={{ opacity: blockLoading ? 0.6 : 1 }}
              >
                {blockLoading ? (
                  <CircularProgress size={18} color="error" sx={{ flexShrink: 0 }} />
                ) : (
                  <BlockOutlinedIcon sx={{ fontSize: 20, color: "error.main", flexShrink: 0 }} />
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600} color="error.main">
                    {displayContact?.blocked ? "Unblock Contact" : "Block Contact"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {displayContact?.blocked
                      ? "Allow messages from this contact"
                      : "Stop receiving messages from this contact"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Drawer>

      <ContactModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        contactId={contact?.id}
      />
    </>
  );
};

const DrawerSkeleton = ({ classes }) => (
  <Box className={classes.scrollContent}>
    <Box className={classes.profileSection}>
      <Skeleton variant="circular" width={88} height={88} />
      <Skeleton width={140} height={24} />
      <Skeleton width={100} height={20} />
      <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
        <Skeleton variant="rounded" width={70} height={30} />
        <Skeleton variant="rounded" width={70} height={30} />
      </Box>
    </Box>
    <Box className={classes.section}>
      <Skeleton width={80} height={16} sx={{ mb: 1.5 }} />
      {[1, 2].map(i => (
        <Box key={i} sx={{ display: "flex", gap: 1, py: 0.75 }}>
          <Skeleton variant="circular" width={18} height={18} />
          <Skeleton width="70%" height={18} />
        </Box>
      ))}
    </Box>
    <Divider />
    <Box sx={{ px: 2, py: 1.5 }}>
      <Skeleton height={40} variant="rounded" />
    </Box>
    <Divider />
    <Box sx={{ p: 2 }}>
      <Skeleton height={36} variant="rounded" sx={{ mb: 0.5 }} />
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "3px", mt: 1.5 }}>
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} variant="rounded" sx={{ aspectRatio: "1", height: "auto" }} />
        ))}
      </Box>
    </Box>
  </Box>
);

export default ContactDrawer;
